/* ============================================
   SAIF MOTION — Global scroll-triggered reveals
   Auto-applies [data-saif-motion] to common elements
   and observes them with IntersectionObserver.
   ============================================ */
(function () {
  'use strict';

  var prefersReduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  if (prefersReduce) return;

  /**
   * Selectors that should fade-in on scroll. We avoid double-tagging anything
   * that already opted into the homepage's saif-reveal/saif-stagger systems.
   *
   * Booking: only the eyebrow / subheading / progress / nav are tagged.
   * The h1 belongs to the word-stagger system, the bracket to the SVG
   * stroke draw, and each .saif-booking__step keeps its own fade-up
   * keyframe because it re-runs on every step change (tagging the steps
   * or the whole `__inner > *` range gave them two competing entrances).
   */
  var REVEAL_SELECTORS = [
    'main h1', 'main h2', 'main h3',
    '.section .page-width > h2',
    '.section .page-width > .title-wrapper',
    '.collection-hero',
    '.collection-list-wrapper',
    '.featured-collection .title-wrapper',
    '.product__info-wrapper',
    '.product__media-wrapper',
    '.banner__content',
    '.image-with-text__media',
    '.image-with-text__text-item',
    '.multicolumn-card',
    '.rich-text__wrapper',
    '.contact__fields',
    '.template-page__title',
    '.template-page__content',
    '.article-template__title',
    '.article-template__content',
    '.saif-booking__eyebrow',
    '.saif-booking__subheading',
    '.saif-booking__progress',
    '.saif-booking__nav',
    '.saif-glossary__inner > *',
    '.saif-community-wall__inner > *',
    '.saif-my-courses__inner > *',
    '.saif-thank-you__inner > *'
  ];

  /**
   * Headings that get the word-by-word stagger entrance (initHeadingStagger).
   * The word stagger IS the entrance for these elements — they must never
   * also receive the [data-saif-motion] block fade, or the words rise
   * twice (once with the block, once on their own).
   */
  var HEADING_STAGGER_SELECTORS = [
    '.saif-collection-hero__heading',
    '.saif-booking__heading',
    '.saif-lesson__heading',
    '.saif-instructor__heading',
    '.saif-glossary__heading',
    '.saif-thank-you__heading',
    '.saif-courses__heading'
  ];
  var HEADING_STAGGER_SELECTOR = HEADING_STAGGER_SELECTORS.join(', ');

  /**
   * Selectors whose direct children should stagger-reveal as the parent enters view.
   */
  var STAGGER_SELECTORS = [
    '.product-grid',
    '.collection-list',
    '.multicolumn-list',
    '.contact__fields',
    '.featured-collection .product-grid',
    '.list-menu',
    '.saif-glossary__terms',
    '.saif-community-wall__posts'
  ];

  /**
   * True when the element declares its own (finite) CSS keyframe entrance —
   * e.g. the hero's saif-hero-rise or the lesson page's saif-lesson-fade-up.
   * Such elements must not ALSO be auto-tagged with [data-saif-motion]:
   * two competing entrances make the content visibly animate twice (the
   * delayed keyframe snaps the element back to opacity 0 mid-reveal).
   * Infinite animations (pulses, marquees, spinners) are ambient — they
   * don't count as an entrance. The global page fade is ignored too.
   */
  function hasOwnEntranceAnimation(el) {
    var cs = window.getComputedStyle(el);
    var names = cs.animationName.split(',');
    var counts = cs.animationIterationCount.split(',');
    for (var i = 0; i < names.length; i++) {
      var name = names[i].trim();
      if (name === 'none' || name === 'saif-page-fade-in') continue;
      var count = (counts[i % counts.length] || '').trim();
      if (count !== 'infinite') return true;
    }
    return false;
  }

  // Per-init() cache for the ancestor walk below (elements share ancestors).
  var _entranceAncestorCache = null;

  /**
   * True when any ancestor (up to <main>/<body>) runs its own entrance
   * keyframe — the element will ride that entrance, so auto-tagging it
   * would compound two animations on the same content.
   */
  function insideEntranceAnimation(el) {
    var chain = [];
    var node = el.parentElement;
    var result = false;
    while (node && node.tagName !== 'MAIN' && node.tagName !== 'BODY') {
      if (_entranceAncestorCache.has(node)) {
        result = _entranceAncestorCache.get(node);
        break;
      }
      chain.push(node);
      if (hasOwnEntranceAnimation(node)) {
        result = true;
        break;
      }
      node = node.parentElement;
    }
    for (var i = 0; i < chain.length; i++) _entranceAncestorCache.set(chain[i], result);
    return result;
  }

  // Elements tagged by THIS module (vs. hardcoded in liquid) — lets the
  // dedupe pass below remove only tags we created ourselves.
  var _autoTagged = [];

  function tagReveal(selector) {
    var nodes = document.querySelectorAll(selector);
    nodes.forEach(function (el) {
      if (el.hasAttribute('data-saif-motion')) return;
      // Skip if the element is already inside something using saif-reveal/saif-stagger
      if (el.closest('.saif-reveal') || el.closest('.saif-stagger-item')) return;
      // Explicit opt-out for sections that run their own entrance system.
      if (el.closest('[data-saif-motion-skip]')) return;
      // Hidden subtrees (booking steps 2-4, modals…) are shown by app
      // logic, not scroll — entrance-gating them makes content pop in
      // late and doubles up with whatever animation reveals the parent.
      if (el.closest('[hidden]')) return;
      // Skip if an ancestor already carries an explicit reveal/stagger
      // (sections now hardcode data-saif-motion[-children] in their liquid)
      // — double-tagging would compound the translate distance.
      if (el.parentElement && el.parentElement.closest('[data-saif-motion], [data-saif-motion-children]')) return;
      // Word-stagger headings already have their entrance (saif-word spans).
      if (el.matches(HEADING_STAGGER_SELECTOR)) return;
      // Elements with their own CSS keyframe entrance (or inside one)
      // already animate — a second, observer-driven entrance on top reads
      // as a glitch (booking/lesson headers, the rebuilt homepage hero).
      if (hasOwnEntranceAnimation(el)) return;
      if (insideEntranceAnimation(el)) return;
      el.setAttribute('data-saif-motion', '');
      _autoTagged.push(el);
    });
  }

  /**
   * Selector order can tag a child before its container (e.g. 'main h1'
   * runs before the section-level selectors). When both end up tagged the
   * translate/fade compound. Strip OUR tag from any auto-tagged element
   * that now sits inside another tagged element — never touches tags
   * hardcoded in liquid.
   */
  function dedupeAutoTags() {
    _autoTagged.forEach(function (el) {
      if (!el.hasAttribute('data-saif-motion')) return;
      if (el.parentElement && el.parentElement.closest('[data-saif-motion], [data-saif-motion-children]')) {
        el.removeAttribute('data-saif-motion');
      }
    });
    _autoTagged.length = 0;
  }

  function tagStagger(selector) {
    var nodes = document.querySelectorAll(selector);
    nodes.forEach(function (el) {
      if (el.hasAttribute('data-saif-motion-children')) return;
      el.setAttribute('data-saif-motion-children', '');
    });
  }

  // Module-level reveal observer — only one observer for the whole page.
  // Without this, every shopify:section:load (which fires per-section,
  // multiple times on initial paint) created a new observer that re-watched
  // the same elements. Each scroll fired N times → cascading repaints.
  var _revealObserver = null;

  function init() {
    _entranceAncestorCache = new Map();
    REVEAL_SELECTORS.forEach(tagReveal);
    dedupeAutoTags();
    STAGGER_SELECTORS.forEach(tagStagger);

    // .saif-motion-heading is observed too: it carries the gold-hairline
    // 'draw' accent (::after) that scales in on .saif-motion--in-view.
    var targets = document.querySelectorAll('[data-saif-motion], [data-saif-motion-children], .saif-motion-heading');
    if (!targets.length) return;

    if (!('IntersectionObserver' in window)) {
      targets.forEach(function (el) { el.classList.add('saif-motion--in-view'); });
      return;
    }

    if (!_revealObserver) {
      _revealObserver = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            if (entry.isIntersecting) {
              entry.target.classList.add('saif-motion--in-view');
              // Stagger containers: once the entrance tail has played out
              // (max 300ms delay + 400ms transition), drop the per-child
              // transition-delays so hover effects respond instantly.
              if (entry.target.hasAttribute('data-saif-motion-children')) {
                (function (el) {
                  setTimeout(function () { el.classList.add('saif-motion--settled'); }, 800);
                })(entry.target);
              }
              _revealObserver.unobserve(entry.target);
            }
          });
        },
        { rootMargin: '0px 0px -8% 0px', threshold: 0.08 }
      );
    }
    // Observe only elements we haven't seen before — observer.observe()
    // is a no-op for already-observed elements but the explicit tag is
    // free and makes the intent clear.
    targets.forEach(function (el) {
      if (el._saifObserved) return;
      el._saifObserved = true;
      _revealObserver.observe(el);
    });
  }

  /**
   * Image fade-in on load.
   * Tag <img> elements that haven't already loaded with .saif-image-reveal,
   * then flip them visible on the load event (or immediately if already complete).
   */
  function initImageReveal() {
    var imgs = document.querySelectorAll('main img:not([data-saif-image-touched])');
    imgs.forEach(function (img) {
      img.setAttribute('data-saif-image-touched', '');
      if (img.complete && img.naturalWidth > 0) return; // already loaded — leave alone
      img.classList.add('saif-image-reveal');
      img.addEventListener('load', function () {
        img.classList.add('saif-image-reveal--loaded');
      }, { once: true });
      img.addEventListener('error', function () {
        img.classList.add('saif-image-reveal--loaded');
      }, { once: true });
    });
  }

  /**
   * Animated number counters. Looks for [data-saif-count] elements (auto-tagged
   * onto .saif-credibility__number etc.) and rolls them from 0 to the parsed
   * numeric prefix when they scroll into view. Preserves any non-numeric
   * suffix ("2,400+" → counts to 2,400 then re-appends "+").
   */
  function initCounters() {
    var COUNTER_SELECTORS = ['.saif-credibility__number', '[data-saif-count]'];
    var nodes = [];
    COUNTER_SELECTORS.forEach(function (sel) {
      document.querySelectorAll(sel).forEach(function (el) {
        if (nodes.indexOf(el) === -1) nodes.push(el);
      });
    });
    if (!nodes.length || !('IntersectionObserver' in window)) return;

    function parseTarget(text) {
      // Pull leading number + optional decimal. "2,400+" → 2400, "26" → 26, "4.5★" → 4.5
      var m = String(text).replace(/[, ]/g, '').match(/^(-?\d+(?:\.\d+)?)(.*)$/);
      if (!m) return null;
      var num = parseFloat(m[1]);
      var suffix = m[2] || '';
      // Re-add commas if the original had any (preserve formatting style)
      var hasCommas = /,/.test(text);
      return { num: num, suffix: suffix, hasCommas: hasCommas, decimals: (m[1].split('.')[1] || '').length };
    }

    function format(n, opts) {
      var fixed = n.toFixed(opts.decimals);
      if (opts.hasCommas) fixed = fixed.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
      return fixed + opts.suffix;
    }

    function animate(el, target) {
      var duration = 1200;
      var start = performance.now();
      function tick(now) {
        var t = Math.min(1, (now - start) / duration);
        // Ease-out cubic
        var eased = 1 - Math.pow(1 - t, 3);
        el.textContent = format(target.num * eased, target);
        if (t < 1) requestAnimationFrame(tick);
        else el.textContent = format(target.num, target);
      }
      requestAnimationFrame(tick);
    }

    var observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        var el = entry.target;
        if (el._saifCounted) return;
        var target = parseTarget(el.textContent.trim());
        if (target) {
          el._saifCounted = true;
          el.textContent = format(0, target);
          animate(el, target);
        }
        observer.unobserve(el);
      });
    }, { threshold: 0.4 });

    nodes.forEach(function (el) { observer.observe(el); });
  }

  /**
   * Word-stagger reveal for editorial headings. Wraps each word in a
   * <span class="saif-word"> and fades them in with a stagger when the
   * heading enters view. Skips already-tagged headings.
   */
  function initHeadingStagger() {
    var nodes = [];
    HEADING_STAGGER_SELECTORS.forEach(function (sel) {
      document.querySelectorAll(sel).forEach(function (el) {
        if (el._saifHeadingTagged) return;
        if (el.children.length && el.querySelector('.saif-word')) return; // already done
        nodes.push(el);
      });
    });
    if (!nodes.length) return;

    nodes.forEach(function (h) {
      h._saifHeadingTagged = true;
      // Walk child nodes; preserve nested elements (em, strong, etc.) by
      // wrapping their text content too. We split on whitespace.
      var processNode = function (node) {
        if (node.nodeType === 3) {
          // Text node — split into words
          var frag = document.createDocumentFragment();
          var words = node.textContent.split(/(\s+)/);
          words.forEach(function (w) {
            if (/^\s+$/.test(w)) {
              frag.appendChild(document.createTextNode(w));
            } else if (w.length) {
              var inner = document.createElement('span');
              inner.className = 'saif-word__inner';
              inner.textContent = w;
              var outer = document.createElement('span');
              outer.className = 'saif-word';
              outer.appendChild(inner);
              frag.appendChild(outer);
            }
          });
          node.parentNode.replaceChild(frag, node);
        } else if (node.nodeType === 1) {
          // Element — recurse, but copy children to array first because
          // replaceChild mutates.
          Array.prototype.slice.call(node.childNodes).forEach(processNode);
        }
      };
      Array.prototype.slice.call(h.childNodes).forEach(processNode);

      // Assign incremental delays to each .saif-word
      h.querySelectorAll('.saif-word').forEach(function (w, i) {
        w.style.setProperty('--saif-word-delay', (i * 0.05) + 's');
      });
    });

    if (!('IntersectionObserver' in window)) {
      nodes.forEach(function (h) { h.classList.add('saif-heading--in-view'); });
      return;
    }
    var observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          entry.target.classList.add('saif-heading--in-view');
          observer.unobserve(entry.target);
        }
      });
    }, { threshold: 0.2 });
    nodes.forEach(function (h) { observer.observe(h); });
  }

  /**
   * Bracket ornament draw-in. Animates stroke-dashoffset on the line + diamond
   * paths of the saif-bracket SVG so the ornament draws on as it enters view.
   */
  function initBracketDraw() {
    var brackets = document.querySelectorAll('.saif-booking__bracket svg, .saif-lesson__bracket svg, .saif-collection-hero__bracket svg, .saif-thank-you__bracket svg');
    if (!brackets.length) return;
    brackets.forEach(function (svg) {
      if (svg._saifBracketTagged) return;
      svg._saifBracketTagged = true;
      svg.classList.add('saif-bracket-draw');
      var paths = svg.querySelectorAll('line, path');
      paths.forEach(function (p) {
        var len = (p.getTotalLength && p.getTotalLength()) || 240;
        p.style.strokeDasharray = len;
        p.style.strokeDashoffset = len;
      });
    });

    if (!('IntersectionObserver' in window)) {
      brackets.forEach(function (svg) { svg.classList.add('saif-bracket-draw--in-view'); });
      return;
    }
    var observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          entry.target.classList.add('saif-bracket-draw--in-view');
          observer.unobserve(entry.target);
        }
      });
    }, { threshold: 0.4 });
    brackets.forEach(function (svg) { observer.observe(svg); });
  }

  /**
   * Subtle hero parallax. Translates the .saif-hero__atmosphere layer at 0.4x
   * the scroll position so the radial gradients drift slowly. GPU-accelerated
   * via translate3d. Skipped on small viewports (<750px) and reduced motion.
   */
  function initHeroParallax() {
    if (window.innerWidth < 750) return;
    var atmosphere = document.querySelector('.saif-hero__atmosphere');
    if (!atmosphere) return;
    // Idempotency guard — Shopify fires `shopify:section:load` multiple
    // times on initial paint (and again on theme-editor edits). Without
    // this, every fire stacked a new scroll listener → multiple parallax
    // computations per scroll → visible flicker on the homepage hero.
    if (atmosphere._saifParallaxAttached) return;
    atmosphere._saifParallaxAttached = true;

    var raf = null;
    function update() {
      raf = null;
      var y = window.scrollY * 0.4;
      atmosphere.style.transform = 'translate3d(0, ' + y + 'px, 0)';
    }
    window.addEventListener('scroll', function () {
      if (raf) return;
      raf = requestAnimationFrame(update);
    }, { passive: true });
    update();
  }

  /**
   * Parallax-lite for decorative layers only (never text). Elements tagged
   * [data-saif-parallax] get a vertical translate3d of at most ±12px (24px
   * total range) based on their position in the viewport, so they drift at
   * ~0.9 of scroll speed. Elements tagged [data-saif-parallax-var] get a
   * --saif-parallax-y custom property instead, letting CSS move decorative
   * pseudo-elements (e.g. the final-CTA radial glow). Skipped entirely on
   * touch devices (jank risk) — reduced-motion is handled by the module's
   * top-level early return.
   */
  var _parallaxEls = [];
  var _parallaxAttached = false;

  function initParallaxLite() {
    if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;

    var MAX_SHIFT = 12; // px each way — 24px total range

    document.querySelectorAll('[data-saif-parallax], [data-saif-parallax-var]').forEach(function (el) {
      if (el._saifParallaxLite) return;
      el._saifParallaxLite = true;
      _parallaxEls.push(el);
    });
    if (!_parallaxEls.length) return;

    function update() {
      _parallaxRaf = null;
      var vh = window.innerHeight;
      for (var i = 0; i < _parallaxEls.length; i++) {
        var el = _parallaxEls[i];
        var rect = el.getBoundingClientRect();
        if (rect.bottom < -MAX_SHIFT || rect.top > vh + MAX_SHIFT) continue; // off-screen
        // -1 (just left top) … 0 (centered) … 1 (just entered bottom)
        var progress = (rect.top + rect.height / 2 - vh / 2) / (vh / 2 + rect.height / 2);
        if (progress > 1) progress = 1;
        else if (progress < -1) progress = -1;
        var shift = (progress * MAX_SHIFT).toFixed(1);
        if (el.hasAttribute('data-saif-parallax-var')) {
          el.style.setProperty('--saif-parallax-y', shift + 'px');
        } else {
          el.style.transform = 'translate3d(0, ' + shift + 'px, 0)';
        }
      }
    }

    var _parallaxRaf = null;
    function requestUpdate() {
      if (_parallaxRaf) return;
      _parallaxRaf = requestAnimationFrame(update);
    }

    if (!_parallaxAttached) {
      _parallaxAttached = true;
      window.addEventListener('scroll', requestUpdate, { passive: true });
      window.addEventListener('resize', requestUpdate, { passive: true });
    }
    requestUpdate();
  }

  /**
   * Sliding active-tab indicator. Any [role="tablist"] gets a single absolute-
   * positioned span under whichever tab is currently active, smoothly sliding
   * between tabs instead of the indicator jumping.
   */
  function initTabIndicators() {
    var tablists = document.querySelectorAll('[role="tablist"]');
    if (!tablists.length) return;

    tablists.forEach(function (list) {
      if (list._saifIndicatorAttached) return;
      list._saifIndicatorAttached = true;

      // Make sure the tablist can host an absolute child
      var cs = window.getComputedStyle(list);
      if (cs.position === 'static') list.style.position = 'relative';

      var indicator = document.createElement('span');
      indicator.className = 'saif-tab-indicator';
      indicator.setAttribute('aria-hidden', 'true');
      list.appendChild(indicator);

      function findActive() {
        return list.querySelector('[aria-selected="true"]')
            || list.querySelector('.saif-instructor__tab--active')
            || list.querySelector('.saif-practice__tab--active')
            || list.querySelector('[class*="--active"]');
      }

      function update() {
        var active = findActive();
        if (!active) {
          indicator.style.opacity = '0';
          return;
        }
        var listRect = list.getBoundingClientRect();
        var tabRect = active.getBoundingClientRect();
        var left = tabRect.left - listRect.left;
        indicator.style.opacity = '1';
        indicator.style.width = tabRect.width + 'px';
        indicator.style.transform = 'translate3d(' + left + 'px, 0, 0)';
      }

      // Initial position (after layout settles)
      requestAnimationFrame(update);
      // Re-position on click (covers all click-driven tab toggles)
      list.addEventListener('click', function () { setTimeout(update, 30); });
      // Re-position on aria-selected mutation (covers programmatic switches)
      var mo = new MutationObserver(update);
      mo.observe(list, { attributes: true, attributeFilter: ['aria-selected', 'class'], subtree: true });
      // Re-position on resize
      window.addEventListener('resize', update, { passive: true });
    });
  }

  /**
   * Scroll progress thread. A 2px gold line at the very top of the viewport
   * that fills 0 → 100% as the document scrolls. GPU-only via scaleX.
   */
  function initScrollThread() {
    if (document.querySelector('.saif-scroll-thread')) return;
    var thread = document.createElement('div');
    thread.className = 'saif-scroll-thread';
    thread.setAttribute('aria-hidden', 'true');
    document.body.appendChild(thread);

    var raf = null;
    function update() {
      raf = null;
      var doc = document.documentElement;
      var scrollable = doc.scrollHeight - window.innerHeight;
      var pct = scrollable > 0 ? Math.min(1, Math.max(0, window.scrollY / scrollable)) : 0;
      thread.style.transform = 'scaleX(' + pct + ')';
    }
    window.addEventListener('scroll', function () {
      if (raf) return;
      raf = requestAnimationFrame(update);
    }, { passive: true });
    window.addEventListener('resize', update, { passive: true });
    update();
  }

  /**
   * 3D tilt on hover for course cards. Subtle 2° max rotation. Disabled on
   * touch / small screens — pointer:fine media queries cover most of it.
   */
  function initCardTilt() {
    if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;
    var TILT_SELECTORS = ['.saif-course-card', '.saif-instructor__rec-card', '.saif-instructor__booking-card'];
    var cards = [];
    TILT_SELECTORS.forEach(function (sel) {
      document.querySelectorAll(sel).forEach(function (c) { cards.push(c); });
    });

    cards.forEach(function (card) {
      if (card._saifTiltAttached) return;
      card._saifTiltAttached = true;
      card.style.transformStyle = 'preserve-3d';
      var raf = null, rx = 0, ry = 0;
      function apply() {
        raf = null;
        card.style.transform = 'perspective(900px) rotateX(' + rx + 'deg) rotateY(' + ry + 'deg) translateY(-4px)';
      }
      card.addEventListener('mousemove', function (e) {
        var rect = card.getBoundingClientRect();
        var px = (e.clientX - rect.left) / rect.width - 0.5;
        var py = (e.clientY - rect.top) / rect.height - 0.5;
        // Max ~2deg tilt — kept barely-there for the premium-calm voice
        ry = px * 2;
        rx = -py * 1.75;
        if (raf) return;
        raf = requestAnimationFrame(apply);
      }, { passive: true });
      card.addEventListener('mouseleave', function () {
        rx = 0; ry = 0;
        card.style.transform = '';
      });
    });
  }

  function start() {
    init();
    initImageReveal();
    initCounters();
    initHeadingStagger();
    initBracketDraw();
    initHeroParallax();
    initParallaxLite();
    initTabIndicators();
    initScrollThread();
    initCardTilt();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }

  // Re-run on Shopify section reload (theme editor)
  document.addEventListener('shopify:section:load', start);
})();
